import API_CONFIG from '../config/api.config';

const BASE_URL = API_CONFIG.baseURL;

const getToken = () => localStorage.getItem('tura_token');

const clearAuthStorage = () => {
  localStorage.removeItem('tura_token');
  localStorage.removeItem('tura_user');
  localStorage.removeItem('admin_impersonation');
  localStorage.removeItem('admin_original_token');
  localStorage.removeItem('admin_original_user');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('tura-auth-expired'));
  }
};

const buildHeaders = (extra = {}, isJson = true) => ({
  ...(isJson ? { 'Content-Type': 'application/json' } : {}),
  ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0',
  ...extra,
});

const handleJson = async (response, requestToken) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401 && getToken() === requestToken) {
      clearAuthStorage();
    }
    const err = new Error(data.detail || data.message || 'Something went wrong');
    err.status = response.status;
    err.authExpired = response.status === 401;
    throw err;
  }
  return data;
};

const request = (path, options = {}) => {
  const requestToken = getToken();
  return fetch(`${BASE_URL}${path}`, options).then(response => handleJson(response, requestToken));
};

const triggerDownload = async (response, fallbackName) => {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Download failed');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] || fallbackName;
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
  return { success: true, filename };
};

export const authAPI = {
  login: (email, password) =>
    request('/auth/login', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ email, password }),
    }),
  register: (data) =>
    request('/auth/register', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  verify: () => request('/auth/verify', { headers: buildHeaders({}, false) }),
  logout: () => {
    clearAuthStorage();
  },
};

export const textAPI = {
  detectLanguage: (text) =>
    request('/detect-language', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
  getStats: (text, options = {}) =>
    request('/text-stats', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
      signal: options.signal,
    }),
  getActions: (text, targetLang = 'eng') =>
    request('/text/actions', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, target_lang: targetLang }),
    }),
};

export const grammarAPI = {
  check: (text, language = 'en-US', options = {}) =>
    request('/grammar/check', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, language }),
      signal: options.signal,
    }),
  checkPublic: (text) =>
    request('/grammar/check-public', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
  spellCheck: (text, language = 'en-US', options = {}) =>
    request('/spell/check', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, language }),
      signal: options.signal,
    }),
  resetSpellSession: () =>
    request('/spell/reset-session', {
      method: 'POST',
      headers: buildHeaders(),
    }),
  convertDialectParagraph: (text, direction = 'us_to_uk', options = {}) =>
    request('/dialect/convert', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, direction }),
      signal: options.signal,
    }),
  convertDialectDocument: (text, direction = 'us_to_uk', options = {}) =>
    request('/dialect/convert', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, direction }),
      signal: options.signal,
    }),
};

export const aiAPI = {
  suggest: (text, type = 'improve') =>
    request('/ai/suggest', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, type }),
    }),
  detectTone: (text) =>
    request('/ai/tone-detect', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
  generate: (text, tool_type, settings = {}, options = {}) =>
    request('/ai/generate', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, tool_type, settings, stream: Boolean(settings?.stream) }),
      signal: options.signal,
    }),
  summarize: (text, length = 'medium', format = 'paragraph', stream = true) =>
    request('/ai/summarize', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, length, format, stream }),
    }),
  grammarSpell: (text, language = 'en-US') =>
    request('/ai/grammar-spell', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, language }),
    }),
  style: (text) =>
    request('/ai/style', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
  tone: (text) =>
    request('/ai/tone', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
};

export const styleAnalysisAPI = {
  analyze: (text) =>
    request('/style-analysis/analyze', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text }),
    }),
};

export const documentsAPI = {
  getAll: () => request('/documents', { headers: buildHeaders({}, false) }),
  getById: (id, options = {}) => request(`/documents/${id}`, { headers: buildHeaders({}, false), signal: options.signal }),
  create: (title, content = '', metadata = null, folder_id = null, options = {}) =>
    request('/documents', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ title, content, metadata, folder_id }),
      signal: options.signal,
    }),
  update: (id, data, options = {}) =>
    request(`/documents/${id}`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
      signal: options.signal,
    }),
  delete: (id) =>
    request(`/documents/${id}`, {
      method: 'DELETE',
      headers: buildHeaders({}, false),
    }),
  getProgress: (id) =>
    request(`/documents/${id}/progress`, {
      headers: buildHeaders({}, false),
    }),
  download: async (id, format = 'txt') => {
    const response = await fetch(`${BASE_URL}/documents/${id}/download?format=${encodeURIComponent(format)}`, {
      headers: buildHeaders({}, false),
    });
    return triggerDownload(response, `document.${format}`);
  },
  exportText: async ({ title, text, format }) => {
    const response = await fetch(`${BASE_URL}/documents/export`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ title, text, format }),
    });
    return triggerDownload(response, `${title || 'document'}.${format}`);
  },
};

export const foldersAPI = {
  getAll: () => request('/folders', { headers: buildHeaders({}, false) }),
  create: (name) =>
    request('/folders', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ name }),
    }),
  update: (id, name) =>
    request(`/folders/${id}`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify({ name }),
    }),
  delete: (id, delete_documents = true) =>
    request(`/folders/${id}?delete_documents=${delete_documents}`, {
      method: 'DELETE',
      headers: buildHeaders({}, false),
    }),
  setDefault: (id) =>
    request(`/folders/${id}/default`, {
      method: 'POST',
      headers: buildHeaders(),
    }),
};

export const usersAPI = {
  getProfile: () => request('/users/profile', { headers: buildHeaders({}, false) }),
  updateProfile: (data) =>
    request('/users/profile', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  changePassword: (data) =>
    request('/users/password', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  getSettings: () => request('/users/settings', { headers: buildHeaders({}, false) }),
  updateSettings: (data) =>
    request('/users/settings', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  requestDeletion: (immediate = false) =>
    request('/users/delete-account', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ immediate }),
    }),
  getStats: () => request('/users/stats', { headers: buildHeaders({}, false) }),
  getHistory: (page = 1, limit = 20, feature = null, preview = false) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (feature) params.set('feature', feature);
    if (preview) params.set('preview', 'true');
    return request(`/users/history?${params.toString()}`, { headers: buildHeaders({}, false) });
  },
  getHistoryEntry: (historyType, id) =>
    request(`/users/history/${encodeURIComponent(historyType)}/${encodeURIComponent(id)}`, { headers: buildHeaders({}, false) }),
  getInvoices: () => request('/users/invoices', { headers: buildHeaders({}, false) }),
  downloadInvoice: async (id) => {
    const response = await fetch(`${BASE_URL}/users/invoices/${id}/download`, {
      headers: buildHeaders({}, false),
    });
    return triggerDownload(response, `invoice-${id}.txt`);
  },
};

export const paymentsAPI = {
  createOrder: (plan, duration = 'monthly') =>
    request('/payments/create-order', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ plan, duration }),
    }),
  verify: (razorpay_order_id, razorpay_payment_id, razorpay_signature) =>
    request('/payments/verify', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ razorpay_order_id, razorpay_payment_id, razorpay_signature }),
    }),
  getHistory: () => request('/payments/history', { headers: buildHeaders({}, false) }),
};

export const plansAPI = {
  getPublicPlans: () => request('/plans', { headers: buildHeaders({}, false) }),
};


export const billingAPI = {
  getSummary: () => request('/billing/summary', { headers: buildHeaders({}, false) }),
  requestCancellation: (reason = '') =>
    request('/billing/cancel-request', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ reason }),
    }),
};

export const adminAPI = {
  getOverview: () => request('/admin/overview', { headers: buildHeaders({}, false) }),
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/admin/users${queryString ? `?${queryString}` : ''}`, { headers: buildHeaders({}, false) });
  },
  getUser: (id) => request(`/admin/user/${id}`, { headers: buildHeaders({}, false) }),
  updatePlan: (id, plan) =>
    request(`/admin/user/${id}/plan`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify({ plan }),
    }),
  updateStatus: (id, is_active) =>
    request(`/admin/user/${id}/status`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify({ is_active }),
    }),
  deleteUser: (id) =>
    request(`/admin/user/${id}`, {
      method: 'DELETE',
      headers: buildHeaders({}, false),
    }),
  loginAsUser: (id) =>
    request(`/admin/user/${id}/login-as`, {
      method: 'POST',
      headers: buildHeaders({}, false),
    }),
  getRevenue: () => request('/admin/revenue', { headers: buildHeaders({}, false) }),
  getInvoices: () => request('/admin/invoices', { headers: buildHeaders({}, false) }),
  createInvoice: (data) =>
    request('/admin/invoices', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  updateInvoiceStatus: (id, status) =>
    request(`/admin/invoices/${id}/status`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify({ status }),
    }),
  getCancellations: () => request('/admin/cancellations', { headers: buildHeaders({}, false) }),
  acceptCancellation: (id, admin_note = '') =>
    request(`/admin/cancellations/${id}/accept`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ admin_note }),
    }),
  getAlerts: () => request('/admin/alerts', { headers: buildHeaders({}, false) }),
  getAPIAnalytics: () => request('/admin/api-analytics', { headers: buildHeaders({}, false) }),
  getIntegrations: () => request('/admin/integrations', { headers: buildHeaders({}, false) }),
  getModels: () => request('/admin/models', { headers: buildHeaders({}, false) }),
  createModel: (data) =>
    request('/admin/models', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  updateModel: (id, data) =>
    request(`/admin/models/${id}`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  activateModel: (id) =>
    request(`/admin/models/${id}/activate`, {
      method: 'POST',
      headers: buildHeaders({}, false),
    }),
  toggleModel: (id) =>
    request(`/admin/models/${id}/toggle`, {
      method: 'POST',
      headers: buildHeaders({}, false),
    }),
  testModel: (id) =>
    request(`/admin/models/${id}/test`, {
      method: 'POST',
      headers: buildHeaders({}, false),
    }),
  getFastCheckModel: () => request('/admin/fast-check-model', { headers: buildHeaders({}, false) }),
  updateFastCheckModel: (data) =>
    request('/admin/fast-check-model', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  testFastCheckModel: () =>
    request('/admin/fast-check-model/test', {
      method: 'POST',
      headers: buildHeaders({}, false),
    }),
  getTranslationModel: (options = {}) => request('/admin/translation-model', { headers: buildHeaders({}, false), signal: options.signal }),
  updateTranslationModel: (data, options = {}) =>
    request('/admin/translation-model', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
      signal: options.signal,
    }),
  testTranslationModel: (options = {}) =>
    request('/admin/translation-model/test', {
      method: 'POST',
      headers: buildHeaders({}, false),
      signal: options.signal,
    }),
  getPlans: () => request('/admin/plans', { headers: buildHeaders({}, false) }),
  createPlan: (data) =>
    request('/admin/plans', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  updatePlanConfig: (id, data) =>
    request(`/admin/plans/${id}`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  deletePlan: (id) =>
    request(`/admin/plans/${id}`, {
      method: 'DELETE',
      headers: buildHeaders({}, false),
    }),
  getSystemSettings: () => request('/admin/system-settings', { headers: buildHeaders({}, false) }),
  updateSystemSettings: (data) =>
    request('/admin/system-settings', {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify(data),
    }),
  setup2FA: () => request('/auth/2fa/setup', { headers: buildHeaders({}, false) }),
  enable2FA: (token) =>
    request('/auth/2fa/enable', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ token }),
    }),
  disable2FA: (token) =>
    request('/auth/2fa/disable', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ token }),
    }),
  get2FAStatus: () => request('/auth/2fa/status', { headers: buildHeaders({}, false) }),
  verify2FA: (tempToken, token) =>
    request('/auth/2fa/verify', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ tempToken, token }),
    }),
};

export const integrationsAPI = {
  getKeys: () => request('/integrations/keys', { headers: buildHeaders({}, false) }),
  createKey: (keyName) =>
    request('/integrations/keys', {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ key_name: keyName }),
    }),
  renameKey: (id, keyName) =>
    request(`/integrations/keys/${id}`, {
      method: 'PUT',
      headers: buildHeaders(),
      body: JSON.stringify({ key_name: keyName }),
    }),
  revokeKey: (id) =>
    request(`/integrations/keys/${id}`, {
      method: 'DELETE',
      headers: buildHeaders({}, false),
    }),
  getStats: () => request('/integrations/stats', { headers: buildHeaders({}, false) }),
  getAnalytics: () => request('/integrations/analytics', { headers: buildHeaders({}, false) }),
};

export const translationAPI = {
  getLanguages: () => request('/translate/languages', { headers: buildHeaders({}, false) }),
  translate: (text, toLang, fromLang = 'auto', options = {}) =>
    request('/translate', {
      ...options,
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ text, to_lang: toLang, from_lang: fromLang }),
    }),
};


